import React, { useState, useRef, useEffect } from 'react';
import { useParams, useNavigate, useLocation } from 'react-router-dom';
import { Send, SquarePen, Trash2, ThumbsUp, ThumbsDown, Bot, User, AlertCircle, BookOpen, Brain, Globe, CheckCircle, ArrowRight } from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';
import ReactMarkdown from 'react-markdown';
import './PublicChatInterface.css';
import './ChatInterface.css';

// Fix malformed markdown links in text before rendering
// Handles cases where URLs contain markdown syntax like ]( or %5D(
const fixMalformedMarkdownLinks = (text) => {
  if (!text || typeof text !== 'string') return text;
  
  let fixed = text;
  
  // Pattern 1: Handle [text](url](url)) or [text](url%5D(url)) with extra closing parens
  fixed = fixed.replace(/\[([^\]]*?)\]\((https?:\/\/[^\)]*?)(?:%5D\(|]\([^\)]*?\)+)\)+/g, (match, linkText, url) => {
    const cleanUrl = url.split(/%5D\(|]\(/)[0].trim();
    const cleanLinkText = linkText.replace(/^\[+/, '').trim();
    return `[${cleanLinkText}](${cleanUrl})`;
  });
  
  // Pattern 2: Handle cases with extra leading brackets: [[text](url](url))
  fixed = fixed.replace(/\[+([^\]]*?)\]\((https?:\/\/[^\)]*?)(?:%5D\(|]\([^\)]*?\)+)\)+/g, (match, linkText, url) => {
    const cleanUrl = url.split(/%5D\(|]\(/)[0].trim();
    const cleanLinkText = linkText.replace(/^\[+/, '').trim();
    return `[${cleanLinkText}](${cleanUrl})`;
  });
  
  // Pattern 3: Clean up any remaining stray brackets/parens around links
  // Remove leading [ that appears right before a markdown link pattern
  fixed = fixed.replace(/\[(\[([^\]]+)\]\(https?:\/\/[^\)]+\))/g, '$1');
  // Remove trailing ) that appears right after a markdown link's closing paren
  // Match: [text](url)) -> [text](url)
  fixed = fixed.replace(/(\[([^\]]+)\]\(https?:\/\/[^\)]+\))\)+/g, (match) => {
    // Only keep one closing paren (the one that closes the markdown link)
    const firstClose = match.indexOf(')');
    return match.substring(0, firstClose + 1);
  });
  
  return fixed;
};

// Fix malformed hrefs that contain markdown syntax (e.g., "url%5D(url)" -> "url")
const fixMalformedHref = (href) => {
  if (!href || typeof href !== 'string') return href;
  // Check if href contains the pattern %5D( which is URL-encoded ](
  const malformedPattern = /%5D\(/;
  if (malformedPattern.test(href)) {
    // Extract just the URL part before the malformed markdown syntax
    return href.split(malformedPattern)[0];
  }
  // Also check for literal ]( pattern (in case it's not URL-encoded)
  const literalPattern = /\]\(/;
  if (literalPattern.test(href)) {
    return href.split(literalPattern)[0];
  }
  return href;
};

const ChatInterface = ({ contextData, contextLoading, uploadedFiles, webpages }) => {
  const { conversationId: urlConversationId } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const [messages, setMessages] = useState([]);
  const [inputMessage, setInputMessage] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [conversationId, setConversationId] = useState(null);
  const [isLoadingHistory, setIsLoadingHistory] = useState(true);
  const [showCorrectionModal, setShowCorrectionModal] = useState(false);
  const [selectedMessage, setSelectedMessage] = useState(null);
  const [correctionText, setCorrectionText] = useState('');
  const [selectedFeedback, setSelectedFeedback] = useState({});
  const [feedbackCount, setFeedbackCount] = useState(0);
  const [submittedFeedback, setSubmittedFeedback] = useState(new Set());
  const [isSubmittingFeedback, setIsSubmittingFeedback] = useState(false);
  const messagesEndRef = useRef(null);
  const { user, getToken } = useAuth();

  // Debug logging - only when props actually change
  useEffect(() => {
    console.log('ChatInterface received contextData:', contextData);
    console.log('ChatInterface received uploadedFiles:', uploadedFiles);
    console.log('UploadedFiles length:', uploadedFiles?.length);
  }, [contextData, uploadedFiles]);

  // Function to create combined context from text input only (not uploaded files)
  // For regular chat, we rely on the vector store for file retrieval
  const getCombinedContext = () => {
    // Only include freeform text context, not uploaded files
    // The vector store will handle retrieving relevant file content
    return contextData || '';
  };

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
  };

  useEffect(() => {
    scrollToBottom();
  }, [messages]);

  // Load conversation from URL parameter
  useEffect(() => {
    if (urlConversationId && urlConversationId !== conversationId) {
      console.log('Loading conversation from URL:', urlConversationId);
      setIsLoadingHistory(true);
      loadConversation(parseInt(urlConversationId));
    }
  }, [urlConversationId]);

  // Load a specific conversation
  const loadConversation = async (conversationIdToLoad, messageIdToScrollTo = null) => {
    try {
      const token = await getToken();
      if (!token) {
        setIsLoadingHistory(false);
        return;
      }
      
      const response = await fetch(`/api/agents/conversations/${conversationIdToLoad}`, {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });
      
      if (response.ok) {
        const data = await response.json();
        if (data.conversation?.messages) {
          setConversationId(conversationIdToLoad);
          
          // Load feedback for this conversation
          const conversationFeedback = await loadConversationFeedback(conversationIdToLoad);
          
          const mappedMessages = data.conversation.messages.map(msg => ({
            id: Date.now() + Math.random(), // Temporary UI ID
            dbId: msg.id, // Real database ID
            type: msg.role === 'user' ? 'user' : 'bot',
            content: msg.content,
            timestamp: new Date(msg.created_at)
          }));
          
          setMessages(mappedMessages);
          
          // Set the feedback state for this conversation
          const feedbackState = {};
          mappedMessages.forEach(msg => {
            if (msg.type === 'bot' && conversationFeedback[msg.dbId]) {
              const feedback = conversationFeedback[msg.dbId];
              if (feedback.rating > 0) {
                feedbackState[msg.id] = 'thumbs_up';
              } else if (feedback.rating < 0) {
                feedbackState[msg.id] = 'thumbs_down';
              }
            }
          });
          setSelectedFeedback(feedbackState);
          
          // Scroll to specific message if messageId is provided
          if (messageIdToScrollTo) {
            // Wait for messages to render, then scroll
            setTimeout(() => {
              const messageElement = document.querySelector(`[data-message-db-id="${messageIdToScrollTo}"]`);
              if (messageElement) {
                messageElement.scrollIntoView({ behavior: 'smooth', block: 'center' });
                // Highlight the message temporarily
                messageElement.style.backgroundColor = 'rgba(0, 102, 255, 0.2)';
                setTimeout(() => {
                  messageElement.style.backgroundColor = '';
                }, 2000);
              }
            }, 300);
          }
        }
      }
    } catch (error) {
      console.error('Error loading conversation:', error);
    } finally {
      setIsLoadingHistory(false);
    }
  };

  // Load feedback count
  const loadFeedbackCount = async () => {
    if (!user?.agent?.id) return;
    
    try {
      const token = await getToken();
      if (!token) return;
      
      const response = await fetch(`/api/agents/feedback/${user.agent.id}`, {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });
      
      if (response.ok) {
        const data = await response.json();
        setFeedbackCount(data.length);
      }
    } catch (error) {
      console.error('Error loading feedback count:', error);
    }
  };

  // Load feedback for a specific conversation
  const loadConversationFeedback = async (conversationId) => {
    if (!user?.agent?.id) return {};
    
    try {
      const token = await getToken();
      if (!token) return {};
      
      const response = await fetch(`/api/agents/feedback/${user.agent.id}`, {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });
      
      if (response.ok) {
        const data = await response.json();
        // Filter feedback for this conversation and create a map by message ID
        const conversationFeedback = {};
        data.forEach(feedback => {
          if (feedback.conversation_id === conversationId) {
            conversationFeedback[feedback.message_id] = {
              rating: feedback.rating,
              feedbackType: feedback.feedback_type
            };
          }
        });
        return conversationFeedback;
      }
    } catch (error) {
      console.error('Error loading conversation feedback:', error);
    }
    return {};
  };


  // Track if we're starting a new chat to prevent auto-load
  const isStartingNewChatRef = useRef(false);

  // Listen for custom events from MainLayout
  useEffect(() => {
    const handleLoadConversation = (event) => {
      console.log('ChatInterface received loadConversation event:', event.detail);
      isStartingNewChatRef.current = false;
      loadConversation(event.detail.conversationId, event.detail.messageId || null);
    };

    const handleStartNewChat = () => {
      console.log('ChatInterface received startNewChat event');
      isStartingNewChatRef.current = true;
      startNewConversation();
      // Reset flag after a short delay to allow useEffect to check it
      setTimeout(() => {
        isStartingNewChatRef.current = false;
      }, 100);
    };

    window.addEventListener('loadConversation', handleLoadConversation);
    window.addEventListener('startNewChat', handleStartNewChat);

    return () => {
      window.removeEventListener('loadConversation', handleLoadConversation);
      window.removeEventListener('startNewChat', handleStartNewChat);
    };
  }, [conversationId]);

  // Feedback handlers
  const handleThumbsUp = async (message) => {
    // Prevent double submission
    if (isSubmittingFeedback) {
      console.log('Already submitting feedback, ignoring duplicate click');
      return;
    }

    if (!user?.agent?.id) {
      console.error('No agent ID available for feedback');
      return;
    }

    // Wait for message to have a dbId (for new messages)
    if (!message.dbId) {
      console.log('Message not yet saved to database, waiting...');
      // Wait a bit and try again
      setTimeout(() => {
        const updatedMessage = messages.find(m => m.id === message.id);
        if (updatedMessage?.dbId) {
          handleThumbsUp(updatedMessage);
        }
      }, 1000);
      return;
    }

    // Create a unique key for this feedback submission
    const feedbackKey = `${message.dbId}-thumbs_up`;
    
    // Check if this feedback has already been submitted
    if (submittedFeedback.has(feedbackKey)) {
      console.log('Thumbs up already submitted for this message');
      return;
    }

    // Toggle selected state
    const messageId = message.id;
    const currentState = selectedFeedback[messageId];
    const isSelected = currentState === 'thumbs_up';
    
    if (isSelected) {
      // Deselect
      setSelectedFeedback(prev => {
        const newState = { ...prev };
        delete newState[messageId];
        return newState;
      });
    } else {
      // Select thumbs up
      setSelectedFeedback(prev => ({
        ...prev,
        [messageId]: 'thumbs_up'
      }));
    }

    // Set loading state
    setIsSubmittingFeedback(true);

    try {
      const token = await getToken();
      if (!token) {
        console.error('No auth token available');
        return;
      }
      
      const response = await fetch(`/api/agents/${user.agent.id}/messages/${message.dbId}/feedback`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({
          feedback: isSelected ? 'down' : 'up'
        })
      });
      
      console.log('Thumbs up feedback payload:', {
        agentId: user.agent.id,
        conversationId: conversationId,
        messageId: message.dbId,
        userQuestion: messages[messages.indexOf(message) - 1]?.content || 'Unknown question'
      });

      if (response.ok) {
        console.log('Thumbs up feedback submitted');
        // Add to submitted feedback set to prevent duplicates
        setSubmittedFeedback(prev => new Set(prev).add(feedbackKey));
        // Update feedback count
        loadFeedbackCount();
      } else {
        console.error('Failed to submit thumbs up feedback:', response.status);
      }
    } catch (error) {
      console.error('Error submitting thumbs up feedback:', error);
    } finally {
      // Always clear loading state
      setIsSubmittingFeedback(false);
    }
  };

  const handleThumbsDown = (message) => {
    // Prevent interaction during feedback submission
    if (isSubmittingFeedback) {
      console.log('Already submitting feedback, ignoring duplicate click');
      return;
    }

    // Toggle selected state
    const messageId = message.id;
    const currentState = selectedFeedback[messageId];
    const isSelected = currentState === 'thumbs_down';
    
    if (isSelected) {
      // Deselect
      setSelectedFeedback(prev => {
        const newState = { ...prev };
        delete newState[messageId];
        return newState;
      });
    } else {
      // Select thumbs down and open correction modal
      setSelectedFeedback(prev => ({
        ...prev,
        [messageId]: 'thumbs_down'
      }));
      setSelectedMessage(message);
      setShowCorrectionModal(true);
    }
  };

  const handleSubmitCorrection = async () => {
    console.log('Submit correction clicked');
    console.log('correctionText:', correctionText);
    console.log('selectedMessage:', selectedMessage);
    console.log('user:', user);
    console.log('conversationId:', conversationId);
    
    // Prevent double submission
    if (isSubmittingFeedback) {
      console.log('Already submitting feedback, ignoring duplicate click');
      return;
    }
    
    if (!correctionText.trim() || !selectedMessage) {
      console.log('Missing correction text or selected message');
      return;
    }
    
    if (!user?.agent?.id) {
      console.error('No agent ID available for feedback');
      return;
    }

    // Wait for message to have a dbId (for new messages)
    if (!selectedMessage.dbId) {
      console.log('Message not yet saved to database, waiting...');
      // Wait a bit and try again
      setTimeout(() => {
        const updatedMessage = messages.find(m => m.id === selectedMessage.id);
        if (updatedMessage?.dbId) {
          setSelectedMessage(updatedMessage);
          handleSubmitCorrection();
        }
      }, 1000);
      return;
    }

    // Create a unique key for this feedback submission
    const feedbackKey = `${selectedMessage.dbId}-${correctionText.trim()}`;
    
    // Check if this feedback has already been submitted
    if (submittedFeedback.has(feedbackKey)) {
      console.log('Feedback already submitted for this message');
      return;
    }

    // Add to submitted feedback set to prevent duplicates
    setSubmittedFeedback(prev => new Set(prev).add(feedbackKey));

    console.log('Submitting correction:', {
      agentId: user.agent.id,
      conversationId: conversationId,
      messageId: selectedMessage.dbId || selectedMessage.id,
      selectedMessage: selectedMessage,
      correctionText: correctionText
    });

    // Set loading state
    setIsSubmittingFeedback(true);

    try {
      const token = await getToken();
      if (!token) {
        console.error('No auth token available');
        return;
      }
      
      const response = await fetch(`/api/agents/${user.agent.id}/messages/${selectedMessage.dbId}/feedback`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({
          feedback: 'down',
          correctedResponse: correctionText.trim()
        })
      });

      if (response.ok) {
        console.log('Correction feedback submitted');
        setShowCorrectionModal(false);
        setSelectedMessage(null);
        setCorrectionText('');
        // Update feedback count
        loadFeedbackCount();
      } else {
        console.error('Failed to submit correction feedback:', response.status);
      }
    } catch (error) {
      console.error('Error submitting correction feedback:', error);
    } finally {
      // Always clear loading state
      setIsSubmittingFeedback(false);
    }
  };

  const handleCloseModal = () => {
    setShowCorrectionModal(false);
    setSelectedMessage(null);
    setCorrectionText('');
    setIsSubmittingFeedback(false);
  };


  // Load feedback count when component mounts
  useEffect(() => {
    if (user?.agent?.id) {
      loadFeedbackCount();
    }
  }, [user?.agent?.id]);

  // Start new conversation when on /build without a conversationId
  useEffect(() => {
    // If there's a conversationId in the URL, don't start a new conversation
    if (urlConversationId) {
      setIsLoadingHistory(false);
      return;
    }

    // If we're on /build without a conversationId, start a new conversation
    if (!conversationId && messages.length === 0) {
      setIsLoadingHistory(false);
      startNewConversation();
    }
  }, [user, urlConversationId]);

  // Removed redundant system message logic - now handled in startNewConversation


  const startNewConversation = async () => {
    // Clear current conversation
    setMessages([]);
    setConversationId(null);
    setInputMessage('');
    setIsLoading(false);
    
    // Add the system message for the new conversation
    setMessages([{
      id: Date.now(),
      type: 'system',
      content: 'Your AI agent is ready! I\'ve learned from your uploaded context and files and can now help answer questions based on your expertise.',
      timestamp: new Date()
    }]);
  };

  const deleteCurrentConversation = async () => {
    if (!conversationId) return;
    
    try {
      const token = await getToken();
      if (!token) return;
      
      const response = await fetch(`/api/agents/conversations/${conversationId}`, {
        method: 'DELETE',
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });
      
      if (response.ok) {
        // Clear current conversation
        setMessages([]);
        setConversationId(null);
        setInputMessage('');
        setIsLoading(false);
        
        // Dispatch event to refresh conversation list
        window.dispatchEvent(new CustomEvent('conversationDeleted', { 
          detail: { conversationId } 
        }));
      }
    } catch (error) {
      console.error('Error deleting conversation:', error);
    }
  };


  const addMessageToConversation = async (role, content, forceConversationId = null) => {
    try {
      console.log('=== addMessageToConversation called ===');
      console.log('Role:', role);
      console.log('Content:', content);
      console.log('Force conversation ID:', forceConversationId);
      console.log('Force conversation ID type:', typeof forceConversationId);
      console.log('Current conversation ID state:', conversationId);
      console.log('Current conversation ID state type:', typeof conversationId);
      
      let currentConvId = forceConversationId || conversationId;
      console.log('Using conversation ID:', currentConvId);
      console.log('Using conversation ID type:', typeof currentConvId);
      
      // Convert to string for comparison
      const currentConvIdStr = String(currentConvId);
      
      // If no conversation exists, create one first
      if (!currentConvId || currentConvIdStr.startsWith('temp-')) {
        const token = await getToken();
        if (!token) {
          throw new Error('No auth token available');
        }
        
        const createResponse = await fetch('/api/agents/conversations', {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${token}`
          },
          body: JSON.stringify({
            title: role === 'user' ? 
              (content.length > 50 ? content.substring(0, 50) + '...' : content) : 
              'New Conversation'
          })
        });
        
        if (createResponse.ok) {
          const createData = await createResponse.json();
          currentConvId = createData.conversation.id;
          setConversationId(currentConvId);
          // Dispatch event to refresh conversation list
          window.dispatchEvent(new CustomEvent('conversationCreated', { 
            detail: { conversationId: currentConvId } 
          }));
        } else {
          const errorText = await createResponse.text();
          console.error('Failed to create conversation:', createResponse.status, errorText);
          throw new Error(`Failed to create conversation: ${createResponse.status} - ${errorText}`);
        }
      }
      
      // Now add the message to the conversation
      const token = await getToken();
      if (!token) {
        throw new Error('No auth token available');
      }
      
      const response = await fetch(`/api/agents/conversations/${currentConvId}/messages`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({
          role,
          content
        })
      });
      
      if (response.ok) {
        const data = await response.json();
        console.log('Message saved successfully:', data);
        return {
          messageId: data.message.id,
          conversationId: data.message.conversation_id
        };
      } else {
        const errorData = await response.json().catch(() => ({}));
        console.error('Failed to save message:', response.status, errorData);
        throw new Error(`Failed to save message: ${response.status} - ${errorData.error || 'Unknown error'}`);
      }
    } catch (error) {
      console.error('Error saving message:', error);
      return null;
    }
  };

  const handleSendMessage = async () => {
    console.log('handleSendMessage')
    if (!inputMessage.trim()) return;
  
    const combinedContext = getCombinedContext();
    const hasTextContext = contextData && contextData.trim().length > 0;
    const hasFileContext = uploadedFiles && uploadedFiles.length > 0;
    const hasWebpageContext = webpages && webpages.length > 0;
  
    if (!hasTextContext && !hasFileContext && !hasWebpageContext) {
      alert('Please add some context (text, files, or webpages) before chatting with your agent.');
      return;
    }
  
    const userMessage = {
      id: Date.now(), // Temporary ID for UI
      type: 'user',
      content: inputMessage,
      timestamp: new Date()
    };

    console.log('userMessage', inputMessage);
    console.log('User:', user);
    const token = await getToken();
    console.log('Token:', token ? 'present' : 'missing');

    setMessages(prev => [...prev, userMessage]);
    setInputMessage('');
    setIsLoading(true);

    // Add message to conversation (this will create the conversation if it doesn't exist)
    const userMessageResult = await addMessageToConversation('user', inputMessage);
    
    if (!userMessageResult) {
      console.error('Failed to save user message, aborting bot response');
      setIsLoading(false);
      return;
    }
    
    const userMessageId = userMessageResult.messageId;
    const currentConversationId = userMessageResult.conversationId;
    
    console.log('User message saved successfully:', { userMessageId, currentConversationId });
    
    // Update the user message with the real database ID
    if (userMessageId) {
      setMessages(prev => prev.map(msg => 
        msg.id === userMessage.id ? { ...msg, dbId: userMessageId } : msg
      ));
    }
  
    try {
      console.log('trying to fetching /api/agents/chat')
      console.log('Request body:', {
        context: combinedContext,
        messages: [
          ...messages.slice(-10).map(m => ({ 
            role: m.type === 'user' ? 'user' : 'assistant', 
            content: m.content 
          })),
          { role: 'user', content: inputMessage }
        ],
        conversationId: conversationId
      });
      
      const token = await getToken();
      if (!token) {
        console.error('No auth token available');
        setIsLoading(false);
        return;
      }
      
      const response = await fetch('/api/agents/chat', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({
          context: combinedContext,
          messages: [
            ...messages.slice(-10).map(m => ({ 
              role: m.type === 'bot' ? 'assistant' : m.type === 'user' ? 'user' : 'system', 
              content: m.content 
            })),
            { role: 'user', content: inputMessage }
          ],
          conversationId: conversationId
        })
      });
  
      const data = await response.json();
      console.log('Chat API response:', data);
      
      if (!response.ok) {
        throw new Error(`API Error: ${response.status} - ${data.error || 'Unknown error'}`);
      }
      
      const botMessage = {
        id: Date.now() + 1, // Temporary ID for UI
        type: 'bot',
        content: data.response || 'Sorry, I encountered an error. Please try again.',
        timestamp: new Date()
      };

      setMessages(prev => [...prev, botMessage]);

      // Save bot message to the same conversation as the user message
      console.log('=== BOT MESSAGE SAVING START ===');
      console.log('Current conversation ID:', currentConversationId);
      console.log('Current conversation ID type:', typeof currentConversationId);
      console.log('Bot message content:', botMessage.content);
      
      const botMessageResult = await addMessageToConversation('assistant', botMessage.content, currentConversationId);
      
      if (botMessageResult) {
        const botMessageId = botMessageResult.messageId;
        console.log('Bot message result:', botMessageResult);
        console.log('Bot message saved with ID:', botMessageId);
        console.log('=== BOT MESSAGE SAVING END ===');
        
        // Update the bot message with the real database ID
        if (botMessageId) {
          setMessages(prev => prev.map(msg => 
            msg.id === botMessage.id ? { ...msg, dbId: botMessageId } : msg
          ));
        }
      } else {
        console.error('Failed to save bot message');
        console.log('=== BOT MESSAGE SAVING END (FAILED) ===');
      }
    } catch (error) {
      console.error('Error sending message:', error);
      setMessages(prev => [...prev, {
        id: Date.now() + 1,
        type: 'error',
        content: 'Sorry, I encountered an error. Please try again.',
        timestamp: new Date()
      }]);
    } finally {
      setIsLoading(false);
    }
  };
  

  const handleKeyPress = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSendMessage();
    }
  };

  if (isLoadingHistory) {
    return (
      <div className="chat-interface">
        <div className="loading-message">
          <p>Loading your conversation history...</p>
        </div>
      </div>
    );
  }

  // Show loading spinner while context is being loaded
  if (contextLoading) {
    return (
      <div className="chat-interface">
        <div className="no-context">
          <div className="loading-spinner" style={{
            width: '40px',
            height: '40px',
            border: '4px solid #f3f3f3',
            borderTop: '4px solid #3498db',
            borderRadius: '50%',
            animation: 'spin 1s linear infinite',
            margin: '0 auto 20px'
          }}></div>
          <h2>Loading Context...</h2>
          <p>Please wait while we load your agent's context.</p>
        </div>
      </div>
    );
  }

  // Check if we have either text context OR uploaded files OR webpages
  const hasContext = (contextData && contextData.trim()) || (uploadedFiles && uploadedFiles.length > 0) || (webpages && webpages.length > 0);
  
  if (!hasContext) {
    return (
      <div className="chat-interface">
        <div className="no-context" style={{
          padding: '3rem 2rem',
          textAlign: 'center',
          maxWidth: '600px',
          margin: '0 auto'
        }}>
          <Bot size={64} style={{ 
            color: 'var(--primary-color, #0066FF)', 
            marginBottom: '1.5rem',
            opacity: 0.8
          }} />
          <h2 style={{ 
            fontSize: '1.75rem', 
            marginBottom: '1rem',
            color: 'var(--text-primary)'
          }}>Get Your Agent Ready</h2>
          <p style={{ 
            fontSize: '1.1rem',
            color: 'var(--text-muted)',
            marginBottom: '2.5rem',
            lineHeight: '1.6'
          }}>
            Follow these steps to create and launch your agent. Or just launch your agent, it's already trained on Pendle documentation and data.
          </p>
          
          <div style={{
            display: 'flex',
            flexDirection: 'column',
            gap: '1.5rem',
            marginBottom: '2rem',
            textAlign: 'left'
          }}>
            {/* Step 1 */}
            <div style={{
              display: 'flex',
              gap: '1rem',
              padding: '1.25rem',
              background: 'var(--bg-secondary, rgba(0, 102, 255, 0.05))',
              borderRadius: '12px',
              border: '1px solid var(--border-color, rgba(0, 102, 255, 0.1))'
            }}>
              <div style={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                width: '40px',
                height: '40px',
                borderRadius: '50%',
                background: 'var(--primary-color, #0066FF)',
                color: 'white',
                flexShrink: 0,
                fontSize: '1.25rem',
                fontWeight: 'bold'
              }}>1</div>
              <div style={{ flex: 1 }}>
                <h3 style={{ 
                  fontSize: '1.1rem',
                  marginBottom: '0.5rem',
                  color: 'var(--text-primary)'
                }}>Add Content</h3>
                <p style={{ 
                  fontSize: '0.95rem',
                  color: 'var(--text-muted)',
                  marginBottom: '0.75rem',
                  lineHeight: '1.5'
                }}>
                  Add knowledge to your agent using one of these methods:
                </p>
                <div style={{
                  display: 'flex',
                  flexDirection: 'column',
                  gap: '0.5rem'
                }}>
                  <button
                    onClick={() => navigate('/build/upload')}
                    style={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: '0.5rem',
                      padding: '0.5rem 1rem',
                      background: 'transparent',
                      border: '1px solid var(--border-color)',
                      borderRadius: '8px',
                      color: 'var(--text-primary)',
                      cursor: 'pointer',
                      fontSize: '0.9rem',
                      transition: 'all 0.2s'
                    }}
                    onMouseOver={(e) => {
                      e.currentTarget.style.background = 'var(--bg-hover, rgba(0, 102, 255, 0.1))';
                      e.currentTarget.style.borderColor = 'var(--primary-color, #0066FF)';
                    }}
                    onMouseOut={(e) => {
                      e.currentTarget.style.background = 'transparent';
                      e.currentTarget.style.borderColor = 'var(--border-color)';
                    }}
                  >
                    <BookOpen size={16} />
                    Upload Files
                  </button>
                  <button
                    onClick={() => navigate('/build/text')}
                    style={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: '0.5rem',
                      padding: '0.5rem 1rem',
                      background: 'transparent',
                      border: '1px solid var(--border-color)',
                      borderRadius: '8px',
                      color: 'var(--text-primary)',
                      cursor: 'pointer',
                      fontSize: '0.9rem',
                      transition: 'all 0.2s'
                    }}
                    onMouseOver={(e) => {
                      e.currentTarget.style.background = 'var(--bg-hover, rgba(0, 102, 255, 0.1))';
                      e.currentTarget.style.borderColor = 'var(--primary-color, #0066FF)';
                    }}
                    onMouseOut={(e) => {
                      e.currentTarget.style.background = 'transparent';
                      e.currentTarget.style.borderColor = 'var(--border-color)';
                    }}
                  >
                    <Brain size={16} />
                    Add Text
                  </button>
                  {user?.beta_tester && (
                    <button
                      onClick={() => navigate('/build/websites')}
                      style={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: '0.5rem',
                        padding: '0.5rem 1rem',
                        background: 'transparent',
                        border: '1px solid var(--border-color)',
                        borderRadius: '8px',
                        color: 'var(--text-primary)',
                        cursor: 'pointer',
                        fontSize: '0.9rem',
                        transition: 'all 0.2s'
                      }}
                      onMouseOver={(e) => {
                        e.currentTarget.style.background = 'var(--bg-hover, rgba(0, 102, 255, 0.1))';
                        e.currentTarget.style.borderColor = 'var(--primary-color, #0066FF)';
                      }}
                      onMouseOut={(e) => {
                        e.currentTarget.style.background = 'transparent';
                        e.currentTarget.style.borderColor = 'var(--border-color)';
                      }}
                    >
                      <Globe size={16} />
                      Scan Websites
                    </button>
                  )}
                </div>
              </div>
            </div>

            {/* Step 2 */}
            <div style={{
              display: 'flex',
              gap: '1rem',
              padding: '1.25rem',
              background: 'var(--bg-secondary, rgba(128, 128, 128, 0.05))',
              borderRadius: '12px',
              border: '1px solid var(--border-color, rgba(128, 128, 128, 0.1))',
              opacity: 0.6
            }}>
              <div style={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                width: '40px',
                height: '40px',
                borderRadius: '50%',
                background: 'var(--text-muted, #666)',
                color: 'white',
                flexShrink: 0,
                fontSize: '1.25rem',
                fontWeight: 'bold'
              }}>2</div>
              <div style={{ flex: 1 }}>
                <h3 style={{ 
                  fontSize: '1.1rem',
                  marginBottom: '0.5rem',
                  color: 'var(--text-primary)'
                }}>Test Your Agent</h3>
                <p style={{ 
                  fontSize: '0.95rem',
                  color: 'var(--text-muted)',
                  lineHeight: '1.5'
                }}>
                  Once you've added content, come back here to chat with your agent and make sure it's working correctly.
                </p>
              </div>
            </div>

            {/* Step 3 */}
            <div style={{
              display: 'flex',
              gap: '1rem',
              padding: '1.25rem',
              background: 'var(--bg-secondary, rgba(128, 128, 128, 0.05))',
              borderRadius: '12px',
              border: '1px solid var(--border-color, rgba(128, 128, 128, 0.1))',
              opacity: 0.6
            }}>
              <div style={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                width: '40px',
                height: '40px',
                borderRadius: '50%',
                background: 'var(--text-muted, #666)',
                color: 'white',
                flexShrink: 0,
                fontSize: '1.25rem',
                fontWeight: 'bold'
              }}>3</div>
              <div style={{ flex: 1 }}>
                <button
                  onClick={() => navigate('/build/agentsettings')}
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: '0.5rem',
                    padding: '0.5rem 1rem',
                    background: 'transparent',
                    border: '1px solid var(--border-color)',
                    borderRadius: '8px',
                    color: 'var(--text-primary)',
                    cursor: 'pointer',
                    fontSize: '1.1rem',
                    fontWeight: 'bold',
                    marginBottom: '0.5rem',
                    transition: 'all 0.2s'
                  }}
                  onMouseOver={(e) => {
                    e.currentTarget.style.background = 'var(--bg-hover, rgba(0, 102, 255, 0.1))';
                    e.currentTarget.style.borderColor = 'var(--primary-color, #0066FF)';
                  }}
                  onMouseOut={(e) => {
                    e.currentTarget.style.background = 'transparent';
                    e.currentTarget.style.borderColor = 'var(--border-color)';
                  }}
                >
                  Launch Your Agent
                </button>
                <p style={{ 
                  fontSize: '0.95rem',
                  color: 'var(--text-muted)',
                  lineHeight: '1.5'
                }}>
                  Go to Agent Settings and mark your agent as public to share it with others.
                </p>
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="public-chat-interface" data-theme="dark">
      {/* Using: Context Bar */}
      <div className="context-bar">
        <div className="context-info">
          <span className="context-label" style={{ color: 'var(--text-primary)' }}>Using:</span>
          {contextData && contextData.trim() && <span className="context-item">Text ({contextData.length} chars)</span>}
          {uploadedFiles && uploadedFiles.length > 0 && (
            <span className="context-item">Files ({uploadedFiles.length})</span>
          )}
          {webpages && webpages.length > 0 && (
            <span className="context-item">Webpages ({webpages.length})</span>
          )}
          {feedbackCount > 0 && (
            <span className="context-item">Feedback ({feedbackCount})</span>
          )}
          {(!contextData || !contextData.trim()) && (!uploadedFiles || uploadedFiles.length === 0) && (!webpages || webpages.length === 0) && feedbackCount === 0 && (
            <span className="context-item no-context">No context available</span>
          )}
        </div>
        <div className="context-controls">
          <button className="context-btn new-chat" onClick={startNewConversation}>
            <SquarePen size={16} />
          </button>
          {conversationId && (
            <button className="context-btn delete" onClick={deleteCurrentConversation}>
              <Trash2 size={14} />
            </button>
          )}
        </div>
      </div>

      {/* Messages List */}
      <div className="messages-list">
        {messages.map((message) => {
          const isLiveDataIntent = typeof message.content === 'string' && message.content.includes('needs_live_data');
          return (
          <div 
            key={message.id} 
            className={`message ${message.type === 'user' ? 'user' : 'assistant'}`}
            data-message-db-id={message.dbId}
          >
            <div className="message-avatar">
              {message.type === 'user' ? (
                <div className="user-avatar-mockup">O</div>
              ) : <Bot size={20} />}
            </div>
            <div className="message-text" style={isLiveDataIntent ? { color: 'red' } : undefined}>
              <div className="message-sender-info">
                <span className="message-sender">
                  {message.type === 'user' ? 'Owner' : 'Agent'}
                </span>
                {message.type === 'bot' && (
                  <div className="feedback-icons">
                    <ThumbsUp 
                      className={`feedback-icon thumbs-up ${selectedFeedback[message.id] === 'thumbs_up' ? 'selected' : ''}`}
                      onClick={() => handleThumbsUp(message)}
                      size={18}
                    />
                    <ThumbsDown 
                      className={`feedback-icon thumbs-down ${selectedFeedback[message.id] === 'thumbs_down' ? 'selected' : ''}`}
                      onClick={() => handleThumbsDown(message)}
                      size={18}
                    />
                  </div>
                )}
              </div>
              {message.type === 'bot' ? (
                <div className="markdown-content">
                  <ReactMarkdown
                    components={{
                      a: ({ node, ...props }) => {
                        const fixedHref = fixMalformedHref(props.href);
                        return <a {...props} href={fixedHref} target="_blank" rel="noopener noreferrer" />;
                      }
                    }}
                  >
                    {fixMalformedMarkdownLinks(message.content)}
                  </ReactMarkdown>
                </div>
              ) : (
                message.content
              )}
            </div>
          </div>
        )})}
        
        {isLoading && (
          <div className="typing-indicator">
            <span></span>
            <span></span>
            <span></span>
          </div>
        )}
        
        <div ref={messagesEndRef} />
      </div>

      {/* Input Area */}
      <div className="input-area">
        <div className="input-container">
          <input
            type="text"
            value={inputMessage}
            onChange={(e) => setInputMessage(e.target.value)}
            placeholder="Ask me anything about your expertise..."
            className="message-input"
            onKeyPress={handleKeyPress}
            disabled={isLoading}
          />
          <button onClick={handleSendMessage} className="send-button">
            <Send size={16} />
          </button>
        </div>
      </div>

      {/* Correction Modal */}
      {showCorrectionModal && (
        <div className="modal-overlay" onClick={handleCloseModal}>
          <div className="correction-modal" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h3>Provide Correction</h3>
              <button className="close-btn" onClick={handleCloseModal}>×</button>
            </div>
            <div className="modal-content">
              <div className="original-response">
                <strong>Original Response:</strong>
                <div className="response-text">{selectedMessage?.content}</div>
              </div>
              <div className="correction-input">
                <label htmlFor="correction">What should the response have been?</label>
                <textarea
                  id="correction"
                  value={correctionText}
                  onChange={(e) => setCorrectionText(e.target.value)}
                  placeholder="Enter the correct response..."
                  rows={4}
                />
              </div>
            </div>
            <div className="modal-actions">
              <button className="cancel-btn" onClick={handleCloseModal}>
                Cancel
              </button>
              <button 
                className="submit-btn" 
                onClick={(e) => {
                  console.log('Button clicked directly');
                  e.preventDefault();
                  e.stopPropagation();
                  handleSubmitCorrection();
                }}
                disabled={!correctionText.trim() || isSubmittingFeedback}
              >
                {isSubmittingFeedback ? (
                  <>
                    <span className="spinner"></span>
                    Submitting...
                  </>
                ) : (
                  'Submit Correction'
                )}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default ChatInterface;
